// frontend/src/screens/DevelopmentScreen.js
import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { fetchProjectDetails } from "../actions/projectActions";
import { deletePost } from "../actions/postActions";
import { POST_DELETE_RESET } from "../constants/postConstants";
import Post from "../components/posts/Post";
import "./DevelopmentScreen.css";

const DevelopmentScreen = () => {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // 1) Get user role from Redux
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  const userRole = userInfo?.user?.role; // e.g. "admin" or "developer"
  const isDeveloper = userRole === "developer";

  // Project + post delete slices
  const projectDetails = useSelector((state) => state.projectDetails);
  const { loading, error, project } = projectDetails;

  const postDelete = useSelector((state) => state.postDelete);
  const {
    loading: deleteLoading,
    error: deleteError,
    postDeleteSuccess,
  } = postDelete;

  // local sort order
  const [sortOrder, setSortOrder] = useState("desc");

  useEffect(() => {
    dispatch(fetchProjectDetails(projectId));
  }, [dispatch, projectId]);

  useEffect(() => {
    if (postDeleteSuccess) {
      dispatch(fetchProjectDetails(projectId));
      dispatch({ type: POST_DELETE_RESET });
    }
  }, [postDeleteSuccess, dispatch, projectId]);

  const sortedPosts = useMemo(() => {
    if (!project?.posts) return [];
    const postsCopy = [...project.posts];
    postsCopy.sort((a, b) => {
      return sortOrder === "desc"
        ? new Date(b.createdAt) - new Date(a.createdAt)
        : new Date(a.createdAt) - new Date(b.createdAt);
    });
    return postsCopy;
  }, [project?.posts, sortOrder]);

  const handleCreatePost = () => {
    navigate(`/create-post/${projectId}`);
  };

  const handleEditPost = (postId) => {
    navigate(`/edit-post/${postId}`);
  };

  const handleDeletePost = (postId) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;
    dispatch(deletePost(postId));
  };

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
  };

  // Render
  return (
    <div className="development-screen-container">
      {loading && <p>Loading project details...</p>}
      {error && <p style={{ color: "red" }}>{error}</p>}
      {deleteLoading && <p>Deleting post...</p>}
      {deleteError && <p style={{ color: "red" }}>{deleteError}</p>}

      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          marginBottom: "1rem",
        }}
      >
        <div>
          <h2 style={{ margin: 0 }}>
            Developer: {project?.developer?.name || "Loading..."}
          </h2>
          <h3 style={{ margin: 0 }}>
            Project: {project?.name || "Loading..."}
          </h3>
        </div>
        <span
          onClick={toggleSortOrder}
          style={{
            marginLeft: "auto",
            color: "blue",
            textDecoration: "underline",
            cursor: "pointer",
            fontSize: "1rem",
          }}
        >
          Sort By Date ({sortOrder === "desc" ? "Newest" : "Oldest"})
        </span>
      </div>

      <p>{project?.description || "Loading project description..."}</p>

      {/* 2) Conditionally render "Create New Post" only if NOT developer */}
      {!isDeveloper && (
        <button onClick={handleCreatePost} style={{ marginBottom: "20px" }}>
          Create New Post
        </button>
      )}

      <div className="posts-list">
        {sortedPosts.length > 0 ? (
          sortedPosts.map((post) => (
            <div key={post._id} className="post-item">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "0.5rem",
                }}
              >
                <div>
                  <h2 style={{ margin: 0 }}>{post.title}</h2>
                  <p className="post-date" style={{ margin: 0 }}>
                    {new Date(post.createdAt).toLocaleDateString()}
                  </p>
                </div>

                {/* 3) Conditionally render Edit/Delete only if NOT developer */}
                {!isDeveloper && (
                  <div style={{ display: "inline-flex", gap: "0.5rem" }}>
                    <button
                      onClick={() => handleEditPost(post._id)}
                      style={{ fontSize: "0.8rem", padding: "0.3rem 0.6rem" }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeletePost(post._id)}
                      style={{ fontSize: "0.8rem", padding: "0.3rem 0.6rem" }}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>

              <p>{post.description}</p>
              <Post post={post} />
            </div>
          ))
        ) : (
          <p>No posts available for this project.</p>
        )}
      </div>
    </div>
  );
};

export default DevelopmentScreen;
